/**
 * Searches within an oversized collection without leaving the collection page.
 *
 * Collections above 5,000 products get no facets from Shopify, so this fetches
 * the /search section (scoped by a product filter carried in the form's hidden
 * inputs) and swaps its filters, results and toolbar into the collection grid.
 * The URL stays on /collections/<handle>.
 *
 * Once the facets have been injected, <facet-filters> owns every subsequent
 * request: it rebuilds each one from the #facets form, which carries the query
 * term and the active scope filter forward automatically.
 */
if (!customElements.get('collection-search-scope')) {
  // The device picker writes this filter; the search box and the picker both
  // have to respect it, so it is named once here.
  const DEVICE_FILTER = 'filter.p.m.custom.compability';

  // Shopify's search needs a term: an empty one returns nothing, and a filter
  // alone does not narrow an oversized collection at all. The device name stands
  // in whenever the shopper has not typed their own words. It is never shown in
  // the box and never written to the address bar.
  //
  // It has to be the device name rather than a wildcard. Shopify returns NO
  // facets for a search matching over 1,000 products, and a wildcard matches the
  // whole collection - which is why the filter panel came back empty. The device
  // name is narrow by construction ("Xiaomi 15 Ultra" matches ~45 products), so
  // the panel comes back populated and its counts describe the results shown.

  class CollectionSearchScope extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('.js-collection-search-form');
      this.input = this.querySelector('input[name="q"]');
      this.grid = document.querySelector('custom-pagination.main-products-grid');
      this.results = document.getElementById('filter-results');
      this.toolbar = document.getElementById('products-toolbar');
      this.pristine = null;
      this.gridAttrs = null;

      if (!this.form || !this.input || !this.grid || !this.results) return;

      this.form.addEventListener('submit', this.handleSubmit.bind(this));

      // Fires when the input's native "clear" button is used.
      this.input.addEventListener('search', () => {
        if (this.input.value.trim()) return;

        // Clearing the words must not clear the device: fall back to the
        // device-only view rather than the whole collection.
        if (this.activeDevice()) {
          this.fetchAndSwap(this.buildParams());
        } else {
          this.restore();
        }
      });

      this.results.addEventListener('click', this.handleResultsClick.bind(this));

      // facet-filters.js re-renders the panel through its own request, so the
      // corrections have to be re-applied every time it lands, not only after
      // this element's own swap.
      document.addEventListener('on:facet-filters:updated', () => {
        if (this.dataset.noNativeFilters !== 'true') return;
        this.dropDeviceFacet();
        this.stripFilterCounts();
      });

      // "Clear all" is built by Shopify from the search parameters, so it clears
      // back to the stand-in term's results rather than to the collection. On a
      // scoped collection the shopper means "show me everything here".
      this.grid.addEventListener('click', (evt) => {
        if (!evt.target.closest('.js-clear-all')) return;
        evt.preventDefault();
        window.location = window.location.pathname;
      });
      window.addEventListener('popstate', this.handleHistoryChange.bind(this));

      this.bootstrap();
    }

    /**
     * The device filter currently in the address bar, if any.
     * @returns {string}
     */
    activeDevice() {
      // A collection with its own facets applies this filter natively, with
      // accurate counts and the full value list. Taking it over would replace
      // both with search results that do not match what is on the page.
      if (this.dataset.noNativeFilters !== 'true') return '';
      return new URLSearchParams(window.location.search).get(DEVICE_FILTER) || '';
    }

    /**
     * Restores results when the page is loaded with a query or a picked device
     * in the URL (a reload, a shared link, or a link back into a search).
     */
    bootstrap() {
      const params = new URLSearchParams(window.location.search);
      const term = params.get('q') || '';
      const device = this.activeDevice();

      // The stand-in term only means anything alongside a device; on its own it
      // would ask Shopify for a 9,000-product search that carries no facets.
      if (!device && !term) return;

      // A term equal to the device is this element's own stand-in, not something
      // the shopper typed, so it must not appear in the box.
      this.input.value = term === device ? '' : term;

      // A URL pushed by facet-filters.js already holds the full parameter set,
      // so replay it verbatim rather than rebuilding a bare search.
      const searchParams = params.has('type') ? params : this.buildParams(params.get('page'));
      this.fetchAndSwap(searchParams, false);
    }

    handleSubmit(evt) {
      evt.preventDefault();
      const term = this.input.value.trim();

      // Dismisses the on-screen keyboard, which otherwise covers the results
      // the shopper just asked for. Covers Enter and the submit button alike.
      this.input.blur();

      if (!term && !this.activeDevice()) {
        this.restore();
        return;
      }

      this.fetchAndSwap(this.buildParams());
    }

    /**
     * Handles history changes (e.g. back button clicked).
     *
     * States that carry a query are handled by facet-filters.js, which is
     * listening too - this only needs to catch the return to the unsearched
     * collection.
     */
    handleHistoryChange() {
      if (new URLSearchParams(window.location.search).has('q') || this.activeDevice()) return;
      this.restore(false);
    }

    /**
     * Intercepts pagination links, which point at /search and would otherwise
     * navigate away from the collection.
     * @param {object} evt - Event object.
     */
    handleResultsClick(evt) {
      const link = evt.target.closest('.js-pagination a');
      if (!link) return;

      const facetFilters = document.querySelector('facet-filters');
      const form = document.getElementById('facets');
      if (!facetFilters || !form || typeof facetFilters.applyFilters !== 'function') return;

      // Only intercept while the grid is showing search results.
      const params = new URLSearchParams(new FormData(form));
      if (!params.get('q') && !params.get(DEVICE_FILTER)) return;

      evt.preventDefault();

      const page = new URL(link.href, window.location.origin).searchParams.get('page');
      if (page) {
        params.set('page', page);
      } else {
        params.delete('page');
      }

      facetFilters.applyFilters(params.toString(), evt);
    }

    /**
     * Builds the search parameters from the form, which holds the query term,
     * the result type and the collection's scope filter.
     * @param {string} [page] - Page number to request.
     * @returns {URLSearchParams}
     */
    buildParams(page) {
      const params = new URLSearchParams(new FormData(this.form));
      const term = this.input.value.trim();
      const device = this.activeDevice();

      // The form holds the scope and the term; everything else the shopper has
      // chosen - the device, a sort, a price range - lives in the URL and would
      // be dropped by rebuilding from the form alone.
      new URLSearchParams(window.location.search).forEach((value, name) => {
        if (name === 'q' || name === 'page' || !value) return;
        if (!params.has(name)) params.set(name, value);
      });

      if (device) params.set(DEVICE_FILTER, device);
      params.set('q', term || device);

      if (page) params.set('page', page);
      return params;
    }

    /**
     * Forces the collection's scope filter onto a set of search parameters.
     *
     * Called by facet-filters.js for every request it makes while a search is
     * active, so that no interaction - "clear all" in particular - can widen
     * the results past the collection the shopper is browsing.
     * @param {string} searchParams - Filter/sort search parameters.
     * @returns {string}
     */
    withScope(searchParams) {
      const params = new URLSearchParams(searchParams);

      this.form.querySelectorAll('input[type="hidden"][name^="filter."]').forEach((input) => {
        params.set(input.name, input.value);
      });

      // The device is re-asserted the same way, and for the same reason. Relying
      // on its checkbox in the panel was not enough: Shopify only renders that
      // value when it happens to fall inside the facet it returns, so clearing
      // one filter - or ticking a colour while a word was typed - dropped the
      // shopper's phone and widened the results to the whole collection.
      // "Clear all" is handled separately, by leaving the search entirely.
      const device = this.activeDevice();
      if (device) params.set(DEVICE_FILTER, device);

      return params.toString();
    }

    /**
     * Reduces a request's parameters to the ones worth showing a shopper.
     *
     * The endpoint needs the result type, the collection's scope and a term;
     * none of that is the shopper's choice, and the stand-in term in particular
     * must never be seen or shared. What is left is what they picked.
     * @param {string} searchParams - Filter/sort search parameters.
     * @returns {string}
     */
    visibleParams(searchParams) {
      const params = new URLSearchParams(searchParams);
      const hidden = new Set();
      this.form.querySelectorAll('input[type="hidden"]').forEach((input) => hidden.add(input.name));

      const term = (params.get('q') || '').trim();
      const visible = new URLSearchParams();
      const device = params.get(DEVICE_FILTER) || '';
      if (term && term !== device) visible.set('q', term);

      params.forEach((value, name) => {
        if (name === 'q' || hidden.has(name) || !value) return;
        visible.set(name, value);
      });

      return visible.toString();
    }

    /**
     * Fetches scoped search results and swaps them into the collection grid.
     * @param {URLSearchParams} searchParams - Search parameters.
     * @param {boolean} [updateUrl=true] - Update url with the search term.
     */
    async fetchAndSwap(searchParams, updateUrl = true) {
      const fetchUrl = `${this.dataset.searchUrl}?${searchParams}&section_id=${this.dataset.sectionId}`;

      this.results.classList.add('is-loading');

      try {
        const response = await fetch(fetchUrl);
        if (!response.ok) throw new Error(`Search request failed: ${response.status}`);

        const tmpl = document.createElement('template');
        tmpl.innerHTML = await response.text();

        this.cachePristine();

        // Filters are absent from an oversized collection, so the first search
        // injects the whole panel rather than updating one.
        const newFilters = tmpl.content.querySelector('.main-products-grid__filters');
        const currentFilters = this.grid.querySelector('.main-products-grid__filters');

        if (newFilters && currentFilters) {
          currentFilters.replaceWith(newFilters);
        } else if (newFilters) {
          this.grid.insertAdjacentElement('afterbegin', newFilters);
        } else if (currentFilters) {
          currentFilters.remove();
        }

        const newResults = tmpl.content.getElementById('filter-results');
        this.results.innerHTML = newResults ? newResults.innerHTML : '';

        // The "no results" message sits outside #filter-results in the search
        // section, so it has to be moved across explicitly.
        const noResults = tmpl.content.getElementById('search-no-results');
        if (noResults) this.results.prepend(noResults);

        this.dropDeviceFacet();
        this.stripFilterCounts();
        this.applyGridLayout();

        // Must come after the filters are in place: products-toolbar.js looks
        // for them when it upgrades, and only shows its filter toggle if found.
        const newToolbar = tmpl.content.getElementById('products-toolbar');
        if (newToolbar && this.toolbar) {
          this.toolbar.replaceWith(newToolbar);
          this.toolbar = newToolbar;
          this.rebindSort();
        }

        if (updateUrl) {
          const query = this.visibleParams(searchParams);

          // The full parameter set goes into the history state so that
          // facet-filters.js can replay it on back/forward.
          window.history.pushState(
            { searchParams: searchParams.toString() },
            '',
            query ? `${window.location.pathname}?${query}` : window.location.pathname
          );
        }

        document.dispatchEvent(new CustomEvent('on:facet-filters:updated', { bubbles: true }));
      } catch (error) {
        console.warn(error); // eslint-disable-line
      } finally {
        this.results.classList.remove('is-loading');
      }
    }

    /**
     * Removes the counts from the swapped-in facets.
     *
     * Shopify counts a facet value over the text-matched set rather than the one
     * it returns products for, and the two diverge as the set grows: on mobilskal
     * the colour facet offered "Svart (30)" where applying it returns 62 products,
     * every one of them correct. The number cannot be made right on this path, so
     * it is removed rather than shown wrong.
     *
     * Collections that filter natively keep their counts, which were measured
     * exact - "iPhone 17 (14)" against 14 results, "(38)" against 38, "(23)"
     * against 23.
     */
    stripFilterCounts() {
      const panel = this.grid.querySelector('.main-products-grid__filters');
      if (!panel) return;

      panel.querySelectorAll('.filter__count').forEach((count) => count.remove());
    }

    /**
     * Removes the device facet from the swapped-in panel.
     *
     * Shopify builds a facet's values from everything the *term* matched, before
     * that facet is applied - so searching "Xiaomi 15 Ultra" offers iPhone 15 Pro
     * and OnePlus 15 alongside it, none of which are in the results. Every other
     * facet is computed after the device filter and does describe what is on the
     * page, so only this one goes.
     *
     * No loss: on these collections the picker above the grid is the device
     * switcher, and it holds every model rather than the hundred Shopify caps a
     * facet at.
     *
     * Hidden rather than removed. The checked box in this group is what carries
     * the device through the facets form, so removing it made the next click on
     * any other filter silently drop the device and widen the results.
     */
    dropDeviceFacet() {
      const panel = this.grid.querySelector('.main-products-grid__filters');
      if (!panel) return;

      // Each facet is its own <details class="filter">; take only that one.
      panel.querySelectorAll(`input[name="${DEVICE_FILTER}"]`).forEach((input) => {
        const group = input.closest('details.filter');
        if (group) group.hidden = true;
      });
    }

    /**
     * Gives the swapped-in results the collection grid's own column layout.
     *
     * The search section is fetched on its own, so it renders with its schema
     * defaults rather than the settings this collection was configured with -
     * three cards across where the collection shows five. Copying the list's
     * attributes rather than hardcoding classes means it follows whatever the
     * merchant later picks in the theme editor.
     */
    applyGridLayout() {
      if (!this.gridAttrs) return;

      const list = this.results.querySelector('ul.grid');
      if (!list) return;

      Object.entries(this.gridAttrs).forEach(([name, value]) => {
        if (value === null) {
          list.removeAttribute(name);
        } else {
          list.setAttribute(name, value);
        }
      });
    }

    /**
     * Re-points facet-filters.js at the replacement sort control.
     *
     * It caches `.products-toolbar__sort` when it upgrades, which happens as
     * the filters are injected - a moment before the toolbar is swapped - so
     * the reference it holds is detached. The control is brand new here, so
     * there's no listener to duplicate.
     */
    rebindSort() {
      const facetFilters = document.querySelector('facet-filters');
      if (!facetFilters || !facetFilters.sortingEnabled) return;

      const sortBy = this.toolbar.querySelector('.products-toolbar__sort');
      if (!sortBy) return;

      facetFilters.desktopSortBy = sortBy;
      sortBy.addEventListener('change', facetFilters.handleFilterChange.bind(facetFilters));
    }

    /**
     * Stores the unsearched grid so clearing the search doesn't need a refetch.
     */
    cachePristine() {
      if (this.pristine) return;

      this.pristine = {
        results: this.results.innerHTML,
        toolbar: this.toolbar ? this.toolbar.outerHTML : null
      };

      const list = this.results.querySelector('ul.grid');
      this.gridAttrs = list
        ? {
            class: list.getAttribute('class'),
            'data-filters-open-classes': list.getAttribute('data-filters-open-classes'),
            'data-filters-closed-classes': list.getAttribute('data-filters-closed-classes')
          }
        : null;
    }

    /**
     * Returns the collection to its unsearched state.
     * @param {boolean} [updateUrl=true] - Remove the search term from the url.
     */
    restore(updateUrl = true) {
      this.input.value = '';

      if (this.pristine) {
        this.grid.querySelector('.main-products-grid__filters')?.remove();
        this.results.innerHTML = this.pristine.results;

        if (this.toolbar && this.pristine.toolbar) {
          const tmpl = document.createElement('template');
          tmpl.innerHTML = this.pristine.toolbar;
          const restored = tmpl.content.getElementById('products-toolbar');

          if (restored) {
            this.toolbar.replaceWith(restored);
            this.toolbar = restored;
          }
        }
      }

      // A null state keeps facet-filters.js from trying to replay this entry.
      if (updateUrl) window.history.pushState(null, '', window.location.pathname);
    }
  }

  customElements.define('collection-search-scope', CollectionSearchScope);
}
